import moment from 'moment'
import config from 'constants/config'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL, OFFER_TYPE_VILLA } from 'constants/offer'
import { dateIsBefore } from 'lib/datetime/dateUtils'
import { isConnectionCancellationPolicy, isCancellationPolicy, isWithinChangeOfMindPolicy, isFlexibleCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import { CHANNEL_MANAGER_SYNXIS } from 'constants/reservation'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getBookingProtectionOrderItem, orderHasCancellationProtection } from 'lib/order/orderInsuranceUtils'
import { PAYMENT_METHODS_NOT_ALLOWED_REFUND } from 'constants/payment'
import { isOrderItem } from 'lib/order/orderUtils'

export interface OrderHotelCancellationConfig {
  /**
   * Whether or not the hotel reservation is refundable at all
   */
  isRefundablePolicy: boolean;
  /**
   * Policy that allows cancellation up to "days before check in"
  */
  isFlexiblePolicy: boolean;
  /**
   * The last date the user can cancel and get a refund
   */
  policyDeadline: string;
  /**
   * Whether or not the hotel can use  booking protection (refund protect)
   * to get a refund
   * Note: this is different to travel insurance cancellation protection
   */
  canUseBookingProtection: boolean;
  /**
   * Whether or not the hotel can use cancellation protection (through insurance)
   * to get a refund
   * Note: This is different to refund protect
   */
  canUseCancellationProtection: boolean;
  /**
   * Whether or not the user can process their own refund
   */
  canAutoRefund: boolean;
  /**
  * Whether or not the user can make a request request
  */
  canRequestRefund: boolean;
  /**
   * Whether or not the current hotel can be refunded to the original payment method
   */
  canRefundToOrigin: boolean;
  /**
   * Whether or not the current hotel can be funded to brand credit
   */
  canRefundToCredit: boolean;
  /**
   * Whether or not this current hotel can be given a refund in any way
   * Note: This excludes making a refund request (as it's not an actual refund, yet) or
   * refunds through cancellation/booking protection
   */
  canRefund: boolean;
}
const autoRefundableOfferTypes = new Set<App.OfferType>([OFFER_TYPE_HOTEL, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_VILLA, OFFER_TYPE_BED_BANK])

interface CancellationConfigOptions {
  roomId?: string;
}

function getHotelCancellationConfig(
  item: App.OrderItem | App.OrderBedbankItem,
  order: App.Order,
  options: CancellationConfigOptions = {},
): OrderHotelCancellationConfig {
  const activeBookingProtectionItem = getBookingProtectionOrderItem(order)
  const configs: OrderHotelCancellationConfig = {
    isRefundablePolicy: false,
    policyDeadline: '',
    isFlexiblePolicy: false,
    canUseBookingProtection: false,
    canUseCancellationProtection: false,
    canAutoRefund: false,
    canRequestRefund: false,
    canRefundToOrigin: false,
    canRefundToCredit: false,
    canRefund: false,
  }

  if (isOrderItem(item)) {
    // comped packages can never be refunded, we gave them for free!
    if (item.reservation && !item.isCompedPackage) {
      const cancellationPolicy = item.reservation.cancellationPolicies[0]
      let withinChangeOfMind = false
      if (cancellationPolicy) {
        configs.isFlexiblePolicy = isFlexibleCancellationPolicy(cancellationPolicy)

        if (isConnectionCancellationPolicy(cancellationPolicy)) {
          configs.isRefundablePolicy = !cancellationPolicy.nonRefundable
          configs.policyDeadline = cancellationPolicy.deadline
        } else if (isCancellationPolicy(cancellationPolicy)) {
          if (
            (item.offer.type === OFFER_TYPE_HOTEL && config.ENABLE_REFUND_FOR_FLASH_OFFER) ||
            item.offer.type === OFFER_TYPE_ALWAYS_ON
          ) {
            configs.isRefundablePolicy = cancellationPolicy.type !== 'non-refundable' && cancellationPolicy.type !== 'hidden-cancellation-policy'

            if (cancellationPolicy.start) {
              const ignoreChangeOfMindExclusionPeriod = cancellationPolicy.type === 'post-purchase-seven-days' || configs.isFlexiblePolicy
              if (ignoreChangeOfMindExclusionPeriod) {
                configs.policyDeadline = cancellationPolicy.start
              } else {
                const exclusionPeriod = moment(item.reservation.startDate).subtract(14, 'days')
                configs.policyDeadline = moment.min(exclusionPeriod, moment(cancellationPolicy.start)).format(ISO_DATE_FORMAT)
              }
            }
            withinChangeOfMind = isWithinChangeOfMindPolicy(order.createdAt, item.reservation.startDate)

            if (global.__policy_override__ === 'inside-mind') {
              withinChangeOfMind = true
            } else if (!!global.__policy_override__ && global.__policy_override__ !== 'none') {
              withinChangeOfMind = false
            }
          }
        }

        if (global.__policy_override__ === 'outside-policy') {
          withinChangeOfMind = false
          configs.policyDeadline = moment().format(ISO_DATE_FORMAT)
        }
      }

      /**
       * Cannot make a refund request 2 days before check in date
       */
      const cancellationOutsidePolicyDeadline = moment(item.reservation.startDate).subtract(2, 'days')
      configs.canRequestRefund = moment().isBefore(cancellationOutsidePolicyDeadline)

      const isWithinPolicyDeadline = configs.policyDeadline ? dateIsBefore(new Date(), new Date(configs.policyDeadline)) || withinChangeOfMind : false
      // Refund to original available when:
      // - within change of mind (7 days) and not before 14 days of check in
      // - cancellation policy allows it and policy is not "credits only" type
      configs.canRefundToOrigin = withinChangeOfMind
      configs.canRefundToCredit = isWithinPolicyDeadline

      configs.canUseBookingProtection = !!activeBookingProtectionItem
      configs.canUseCancellationProtection = isWithinPolicyDeadline && orderHasCancellationProtection(order)
    }
  } else {
    const room = item.rooms.find(room => room.roomId === options.roomId) ?? item.rooms[0]
    const policy = room.cancellationPolicies.policies[0]

    configs.isRefundablePolicy = room.refundable || room.partiallyRefundable
    configs.policyDeadline = policy?.start ? moment(policy.start).format(ISO_DATE_FORMAT) : ''

    const isWithinPolicyDeadline = configs.policyDeadline ? dateIsBefore(new Date(), new Date(configs.policyDeadline)) : false

    // Bedbank doesn't support refund requests
    configs.canRequestRefund = false
    // Bedbank always allows both refund types
    configs.canRefundToOrigin = isWithinPolicyDeadline
    configs.canRefundToCredit = isWithinPolicyDeadline

    if (global.__policy_override__ === 'inside-mind') {
      configs.canRefundToOrigin = true
      configs.canRefundToCredit = true
    } else if (global.__policy_override__ === 'outside-policy') {
      configs.canRefundToOrigin = false
      configs.canRefundToCredit = false
    }

    configs.canUseBookingProtection = !!activeBookingProtectionItem
    configs.canUseCancellationProtection = isWithinPolicyDeadline && orderHasCancellationProtection(order)
  }

  configs.canRefund = configs.canRefundToCredit || configs.canRefundToCredit
  const creditsOnlyCancellation = !configs.canRefundToOrigin && configs.canRefundToCredit

  // Only certian offer types can auto refund
  configs.canAutoRefund = autoRefundableOfferTypes.has(item.offer.type) &&
  // make sure we're allowed to refund synxis offers, they are a special channel manager
  (config.ENABLE_REFUND_FOR_SYNXIS_OFFER || (isOrderItem(item) && item.reservation?.channelManager !== CHANNEL_MANAGER_SYNXIS)) &&
  // can't refund orders with flights on them, business rule
  !order.flightItems.length &&
  /// refunding is done by the insurance provider, can't automatically do it
  !activeBookingProtectionItem?.refundInitiatedFromProvider &&
  // finally, if it's only credits we good - otherwise just make sure the payment methods the user used is allowed
  (creditsOnlyCancellation || !order.payments.some(payment => PAYMENT_METHODS_NOT_ALLOWED_REFUND.includes(payment.type)))

  return configs
}

export default getHotelCancellationConfig
